import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MenuItem } from '@openng/optimus-ui/api';
import { isEqual } from 'lodash-es';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';

/** Třída aktivní položky navigace (vzhled v styles/reza-components.css, oddíl 7). */
const ACTIVE_CLASS = 'is-route-active';

/**
 * Položka odpovídá URL přesně, nebo je její předek: `/sablony` je aktivní pro `/sablony/1/editace`,
 * ale ne pro `/sablony-skupiny`.
 */
function isActive(item: MenuItem, url: string): boolean {
  const link = item.routerLink && '/' + item.routerLink;
  return !!link && (url === link || url.startsWith(link + '/'));
}

function markActive(item: MenuItem, active: boolean): MenuItem {
  return { ...item, styleClass: [item.styleClass, active && ACTIVE_CLASS].filter(Boolean).join(' ') };
}

/** Kopie položek s třídou aktivní položky; nadřazená položka je aktivní i přes aktivní podpoložku. */
function markActiveItems(items: MenuItem[], url: string): MenuItem[] {
  return items.map((item) => {
    const active = isActive(item, url) || !!item.items?.some((subItem) => isActive(subItem, url));
    const marked = markActive(item, active);
    return item.items
      ? { ...marked, items: item.items.map((subItem) => markActive(subItem, isActive(subItem, url))) }
      : marked;
  });
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private readonly router = inject(Router);

  private readonly currentUrl$: Observable<string> = this.router.events.pipe(
    startWith(null),
    filter((event) => event === null || event instanceof NavigationEnd),
    map(() => this.router.url),
    distinctUntilChanged()
  );

  /**
   * Položky navigace s třídou `is-route-active` podle aktuální URL. Emituje jen při změně položek
   * nebo aktivní položky – přechod v rámci jedné sekce (např. seznam → detail) menubar nepřekresluje.
   */
  withActiveState(items$: Observable<MenuItem[]>): Observable<MenuItem[]> {
    return combineLatest([items$, this.currentUrl$]).pipe(
      map(([items, url]) => markActiveItems(items, url)),
      distinctUntilChanged(isEqual)
    );
  }
}
